import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';

// MIDI Learn Context
const MIDILearnContext = createContext();

export const useMIDILearn = () => {
  const context = useContext(MIDILearnContext);
  if (!context) {
    throw new Error('useMIDILearn must be used within MIDILearnProvider');
  }
  return context;
};

export const MIDILearnProvider = ({ children }) => {
  const [isLearning, setIsLearning] = useState(false);
  const [waitingForControl, setWaitingForControl] = useState(null);
  const [midiMappings, setMidiMappings] = useState({});
  const [midiDevices, setMidiDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const midiInputRef = useRef(null);
  const callbackRegistry = useRef({});

  // Load mappings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('dj_midi_mappings');
    if (saved) {
      try {
        setMidiMappings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load MIDI mappings:', e);
      }
    }
  }, []);

  // Save mappings to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(midiMappings).length > 0) {
      localStorage.setItem('dj_midi_mappings', JSON.stringify(midiMappings));
    }
  }, [midiMappings]);

  // Initialize MIDI
  useEffect(() => {
    console.log('🎹 Initializing MIDI system...');
    
    if (!navigator.requestMIDIAccess) {
      console.error('❌ Web MIDI API not supported in this browser');
      toast.error('Web MIDI API not supported in this browser');
      return;
    }

    navigator.requestMIDIAccess({ sysex: false })
      .then((midiAccess) => {
        console.log('✓ MIDI access granted');
        
        const inputs = Array.from(midiAccess.inputs.values());
        console.log('📋 MIDI Inputs found:', inputs.length);
        inputs.forEach((input, idx) => {
          console.log(`  ${idx + 1}. ${input.name} (${input.manufacturer || 'Unknown'})`);
        });
        
        setMidiDevices(inputs);
        
        if (inputs.length > 0) {
          toast.success(`🎹 Found ${inputs.length} MIDI device(s) - Ready!`, { duration: 3000 });
          
          // Auto-select and setup first device
          const firstDevice = inputs[0];
          setSelectedDevice(firstDevice);
          setupMIDIListener(firstDevice);
          
          console.log(`✓ Auto-selected: ${firstDevice.name}`);
        } else {
          console.warn('⚠️ No MIDI devices connected');
          toast.info('No MIDI devices connected. Connect a controller to use MIDI Learn.');
        }

        // Listen for device connections/disconnections
        midiAccess.onstatechange = (e) => {
          console.log('🔄 MIDI device state change:', e.port.name, e.port.state);
          
          const updatedInputs = Array.from(midiAccess.inputs.values());
          setMidiDevices(updatedInputs);
          
          if (e.port.state === 'connected' && e.port.type === 'input') {
            toast.success(`✓ MIDI device connected: ${e.port.name}`);
            // Auto-setup newly connected device
            setSelectedDevice(e.port);
            setupMIDIListener(e.port);
          } else if (e.port.state === 'disconnected') {
            toast.info(`MIDI device disconnected: ${e.port.name}`);
          }
        };
      })
      .catch((err) => {
        console.error('❌ MIDI access denied:', err);
        toast.error('MIDI access denied. Please grant permission and reload.');
      });
  }, []);

  const setupMIDIListener = (device) => {
    if (midiInputRef.current) {
      midiInputRef.current.onmidimessage = null;
    }

    device.onmidimessage = (message) => {
      const [status, control, value] = message.data;
      
      // Log ALL incoming MIDI messages for debugging
      console.log('🎹 MIDI Message Received:', {
        status,
        control,
        value,
        raw: Array.from(message.data),
        timestamp: message.timeStamp
      });
      
      // Control Change messages (status 176 for channel 1, 177 for channel 2, etc.)
      // Also accept Note On/Off messages (144-159) for buttons
      const isControlChange = status >= 176 && status <= 191;
      const isNoteMessage = status >= 144 && status <= 159;
      
      if (isControlChange || isNoteMessage) {
        console.log('✓ Valid MIDI message, processing...');
        handleMIDIMessage(control, value, status);
      } else {
        console.log('⚠️ Unhandled MIDI message type:', status);
      }
    };

    midiInputRef.current = device;
    console.log(`✓ MIDI listener setup for: ${device.name}`);
    toast.success(`MIDI listener active: ${device.name}`);
  };

  const handleMIDIMessage = (control, value, status) => {
    console.log('🎛️ handleMIDIMessage called:', { control, value, status, waitingForControl });
    
    if (waitingForControl) {
      // We're in learn mode and waiting for a MIDI control
      console.log('📝 Learning mode: Mapping control...');
      
      // Store mapping WITHOUT callback (serializable)
      const mapping = {
        control,
        status,
        action: waitingForControl.action,
        deck: waitingForControl.deck,
        label: waitingForControl.label
      };

      const key = `${status}-${control}`;
      console.log('✓ Creating mapping:', key, mapping);

      // Register callback separately (not serialized)
      const callbackKey = `${waitingForControl.action}-${waitingForControl.deck}`;
      callbackRegistry.current[callbackKey] = waitingForControl.callback;
      console.log('✓ Registered callback for:', callbackKey);

      setMidiMappings(prev => {
        const updated = {
          ...prev,
          [key]: mapping
        };
        console.log('Updated mappings:', updated);
        return updated;
      });

      // Determine message type for user feedback
      const messageType = status >= 176 && status <= 191 ? 'CC' : 'Note';
      toast.success(`✓ Mapped ${messageType}${control} to ${waitingForControl.label}`, {
        duration: 2000
      });
      
      setWaitingForControl(null);
      console.log('✓ Mapping complete, cleared waitingForControl');
    } else {
      // Execute mapped action
      const key = `${status}-${control}`;
      const mapping = midiMappings[key];
      
      console.log('🎮 Executing mapping:', key, mapping);
      
      if (mapping) {
        // Look up callback from registry
        const callbackKey = `${mapping.action}-${mapping.deck}`;
        const callback = callbackRegistry.current[callbackKey];
        
        console.log('Looking up callback:', callbackKey, 'Found:', !!callback);
        
        if (callback) {
          const normalizedValue = value / 127; // 0-1
          console.log('✓ Calling callback with value:', normalizedValue);
          callback(normalizedValue, value);
        } else {
          console.log('⚠️ No callback registered for:', callbackKey);
        }
      } else {
        console.log('⚠️ No mapping found for:', key);
      }
    }
  };

  const startLearning = () => {
    if (midiDevices.length === 0) {
      toast.error('No MIDI devices connected');
      return;
    }
    setIsLearning(true);
    toast.info('🎹 MIDI Learn Mode Active - Click any control to map');
  };

  const stopLearning = () => {
    setIsLearning(false);
    setWaitingForControl(null);
    toast.info('MIDI Learn Mode Disabled');
  };

  const learnControl = (action, deck, label, callback) => {
    if (!isLearning) return;

    setWaitingForControl({
      action,
      deck,
      label,
      callback
    });

    toast.info(`Touch MIDI controller for: ${label}`, {
      duration: 5000
    });
  };

  const clearMapping = (key) => {
    setMidiMappings(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    toast.info('Mapping cleared');
  };

  const clearAllMappings = () => {
    setMidiMappings({});
    localStorage.removeItem('dj_midi_mappings');
    toast.success('All MIDI mappings cleared');
  };

  const registerCallback = (action, deck, callback) => {
    // Register callback in the registry (separate from mappings)
    const callbackKey = `${action}-${deck}`;
    callbackRegistry.current[callbackKey] = callback;
    console.log('✓ Registered callback for:', callbackKey);
  };

  return (
    <MIDILearnContext.Provider
      value={{
        isLearning,
        startLearning,
        stopLearning,
        learnControl,
        waitingForControl,
        midiMappings,
        midiDevices,
        selectedDevice,
        setSelectedDevice,
        setupMIDIListener,
        clearMapping,
        clearAllMappings,
        registerCallback
      }}
    >
      {children}
    </MIDILearnContext.Provider>
  );
};
